import { useState } from "react";
import { Share2, MessageCircle, Facebook, Linkedin, Link as LinkIcon, Check, X, Mail } from "lucide-react";

// Botones de compartir para posts del blog.
// Trackea el evento `share` en dataLayer para que GTM lo capture como interacción social.

export function pushShareEvent(channel, url) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "share",
    method: channel,
    content_type: "article",
    item_id: url,
  });
}

export function getShareChannels(url, title) {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  return [
    {
      key: "whatsapp",
      label: "WhatsApp",
      Icon: MessageCircle,
      href: `https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`,
      color: "bg-[#25D366] hover:bg-[#1FB955]",
    },
    {
      key: "facebook",
      label: "Facebook",
      Icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      color: "bg-[#1877F2] hover:bg-[#0F62D0]",
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      Icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      color: "bg-[#0A66C2] hover:bg-[#084F98]",
    },
    {
      key: "twitter",
      label: "X",
      Icon: (props) => (
        <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      ),
      href: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
      color: "bg-black hover:bg-neutral-800",
    },
    {
      key: "email",
      label: "Email",
      Icon: Mail,
      href: `mailto:?subject=${encodedTitle}&body=Te comparto este artículo: ${encodedUrl}`,
      color: "bg-brand-primary hover:bg-brand-primaryHover",
    },
  ];
}

export default function ShareButtons({ url, title }) {
  const [copied, setCopied] = useState(false);
  const channels = getShareChannels(url, title);

  const handleShare = (channel) => (e) => {
    pushShareEvent(channel.key, url);
    // Los links normales abren en nueva pestaña, mailto no.
    if (channel.key !== "email") {
      e.preventDefault();
      window.open(channel.href, "_blank", "noopener,noreferrer,width=600,height=520");
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      pushShareEvent("copy_link", url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div
      data-testid="share-buttons"
      className="my-10 rounded-2xl border border-brand-line bg-white/60 p-5"
    >
      <div className="flex items-center gap-2 text-sm text-brand-primary font-outfit font-semibold mb-4">
        <Share2 className="h-4 w-4" />
        ¿Te ha resultado útil? Compártelo con otras familias
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {channels.map(({ key, label, Icon, href, color }) => (
          <a
            key={key}
            href={href}
            onClick={handleShare({ key, href })}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`share-${key}`}
            aria-label={`Compartir en ${label}`}
            className={`${color} inline-flex items-center gap-2 text-white text-xs font-outfit font-medium rounded-full px-3.5 py-2 transition-colors`}
          >
            <Icon className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{label}</span>
          </a>
        ))}
        <button
          type="button"
          onClick={handleCopy}
          data-testid="share-copy-link"
          aria-label="Copiar enlace"
          className="inline-flex items-center gap-2 text-brand-primary text-xs font-outfit font-medium rounded-full px-3.5 py-2 border border-brand-line hover:border-brand-primary transition-colors bg-white"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <LinkIcon className="h-3.5 w-3.5" />}
          <span className="hidden sm:inline">{copied ? "Copiado" : "Copiar enlace"}</span>
        </button>
      </div>
    </div>
  );
}
